import {
  React, useEffect, useRef, useState,
} from 'react';
import {
  InfoWindow, Map, Marker,
} from '@vis.gl/react-google-maps';
import { getPlaceDetails, getPlacePhoto } from '../../services/api';
import lodgingIcon from '../../images/lodging.png';
import diningIcon from '../../images/dining.png';
import '../../styling/travel-map.scss';

function TravelMap({ itinerary, dining, lodging }) {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [lodgings, setLodgings] = useState([]);
  const [diningOptions, setDiningOptions] = useState([]);
  const [mapCenter, setMapCenter] = useState(null);

  const [selectedDays, setSelectedDays] = useState([]);
  const [showLodging, setShowLodging] = useState(true);
  const [showDining, setShowDining] = useState(true);
  const [showItinerary, setShowItinerary] = useState(true);

  const availableDays = Object.keys(itinerary || {});
  const colorPalette = ['red', 'yellow', 'purple', 'orange', 'pink', 'blue', 'green'];
  const dynamicDayColors = availableDays.map((_, index) => `http://maps.google.com/mapfiles/ms/icons/${colorPalette[index % colorPalette.length]}-dot.png`);

  const markerIcons = {
    lodging: lodgingIcon,
    dining: diningIcon,
  };

  const getSmallIcon = (iconUrl) => ({
    url: iconUrl, // Keep the same icon URL
    scaledSize: new window.google.maps.Size(30, 30), // Adjust the size (width, height)
    anchor: new window.google.maps.Point(7, 7), // Adjust position to center marker
    fillOpacity: 1,
  });

  useEffect(() => {
    const fetchLocations = async () => {
      const itineraryEntries = itinerary ? Object.entries(itinerary) : [];
      setSelectedDays(availableDays);
      const locationsPromises = itineraryEntries.flatMap(([day, placeIds], index) => placeIds.map(async (placeId) => {
        const placeDetails = await getPlaceDetails(placeId); // Fetch details for each place ID
        const placePhoto = await getPlacePhoto(placeId);
        return {
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          position: {
            lat: placeDetails?.location?.latitude,
            lng: placeDetails?.location?.longitude,
          },
          address: placeDetails?.formattedAddress,
          day, // Include day for reference
          type: 'places',
          icon: dynamicDayColors[index],
        };
      }));

      try {
        const loc = await Promise.all(locationsPromises);
        setLocations(loc);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };
    const fetchDiningOptions = async () => {
      const diningEntries = dining ? Object.entries(dining) : [];
      const diningOptionsPromises = diningEntries.flatMap(([day, placeIds]) => placeIds.map(async (placeId) => {
        const placeDetails = await getPlaceDetails(placeId); // Fetch details for each place ID
        const placePhoto = await getPlacePhoto(placeId);
        return {
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          position: {
            lat: placeDetails?.location?.latitude,
            lng: placeDetails?.location?.longitude,
          },
          address: placeDetails?.formattedAddress,
          day, // Include day for reference
          type: 'dining',
          icon: markerIcons.dining,
        };
      }));

      try {
        const din = await Promise.all(diningOptionsPromises);
        setDiningOptions(din);
      } catch (error) {
        console.error('Error fetching dining options:', error);
      }
    };
    const fetchLodgingOptions = async () => {
      const lodgingEntries = lodging ? Object.entries(lodging) : [];
      const lodgingOptionsPromises = lodgingEntries.map(async ([index, placeId]) => {
        const placeDetails = await getPlaceDetails(placeId);
        const placePhoto = await getPlacePhoto(placeId);
        return {
          type: 'lodging',
          placeName: placeDetails?.displayName?.text,
          placePhotoUrl: placePhoto,
          position: {
            lat: placeDetails?.location?.latitude,
            lng: placeDetails?.location?.longitude,
          },
          icon: markerIcons.lodging,
          address: placeDetails?.formattedAddress,
        };
      });

      try {
        const lod = await Promise.all(lodgingOptionsPromises);
        setLodgings(lod);
      } catch (error) {
        console.error('Error fetching lodging options:', error);
      }
    };
    fetchLocations();
    fetchDiningOptions();
    fetchLodgingOptions();
  }, [itinerary, dining, lodging]);

  useEffect(() => {
    if (locations.length > 0) {
      setMapCenter(locations[0].position);
    }
  }, [locations]);

  const filteredLocations = locations.filter(
    (location) => showItinerary && selectedDays.includes(location.day),
  );
  const filteredDining = diningOptions.filter(
    (din) => showDining && selectedDays.includes(din.day),
  );
  const filteredLodging = lodgings;

  if (!mapCenter) {
    return <div>Loading map...</div>; // Placeholder or loading indicator
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div className="absolute bottom-4 right-4 z-10 bg-white p-3 rounded-lg shadow-md space-y-2 legend-container">
        <h3 style={{ fontSize: '1em', fontWeight: 'bold', padding: '3px 2px 10px 2px' }}>Filter by Day</h3>
        <div>
          {availableDays.map((day, index) => {
            const dayId = `day-filter-${day}`;
            return (
              <div
                key={day}
                id={dayId}
                className={`flex items-center mb-2 p-1 rounded cursor-pointer legend-item ${selectedDays.includes(day) ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-500'}`}
                onClick={() => setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]))}
              >
                <img src={dynamicDayColors[index]} alt={`Day ${index + 1}`} style={{ width: '20px', height: '20px', marginRight: '8px' }} />
                <span>{`Day ${index + 1}`}</span>
              </div>
            );
          })}
        </div>
      </div>
      <Map
        style={{ width: '100%', height: '100%' }}
        defaultCenter={mapCenter} // TBU
        // center={mapCenter}
        defaultZoom={12}
        minZoom={2}
        gestureHandling="greedy"
        disableDefaultUI
      >
        {[...filteredLocations, ...filteredDining, ...filteredLodging].map((location, index) => (
          <Marker
            key={`${location.placeName}-${index}`}
            position={location.position}
            icon={getSmallIcon(location.icon)}
            onClick={() => setSelectedLocation(location)}
            className="cursor-pointer"
          />
        ))}

        {selectedLocation && (
        <InfoWindow
          position={selectedLocation.position}
          onCloseClick={() => setSelectedLocation(null)}
        >
          <div className="info-window-container">
            <h2 className="info-window-title">{selectedLocation.placeName}</h2>
            <p className="info-window-address">Address: {selectedLocation.address}</p>
            {selectedLocation.placePhotoUrl && (
              <img
                src={selectedLocation.placePhotoUrl}
                alt={selectedLocation.placeName}
                className="info-window-image"
              />
            )}
          </div>
        </InfoWindow>
        )}
      </Map>
    </div>
  );
}

export default TravelMap;
